import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, CreditCard, Truck, Shield, Award, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_DATA = {
  'India': {
    code: '+91',
    states: [
      'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat',
      'Haryana','Himachal Pradesh','Jammu & Kashmir','Jharkhand','Karnataka','Kerala',
      'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha',
      'Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
      'Uttarakhand','West Bengal'
    ]
  },
  'United States': {
    code: '+1',
    states: [
      'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
      'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
      'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
      'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
      'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
      'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
      'Virginia','Washington','West Virginia','Wisconsin','Wyoming'
    ]
  },
  'United Kingdom': {
    code: '+44',
    states: ['England','Scotland','Wales','Northern Ireland']
  },
  'United Arab Emirates': {
    code: '+971',
    states: ['Abu Dhabi','Ajman','Dubai','Fujairah','Ras Al Khaimah','Sharjah','Umm Al Quwain']
  },
  'Singapore': {
    code: '+65',
    states: ['Singapore']
  },
  'Australia': {
    code: '+61',
    states: ['Australian Capital Territory','New South Wales','Northern Territory','Queensland','South Australia','Tasmania','Victoria','Western Australia']
  },
  'Canada': {
    code: '+1',
    states: ['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Nova Scotia','Ontario','Prince Edward Island','Quebec','Saskatchewan']
  },
  'Germany': {
    code: '+49',
    states: ['Baden-Württemberg','Bavaria','Berlin','Brandenburg','Bremen','Hamburg','Hesse','Lower Saxony','Mecklenburg-Vorpommern','North Rhine-Westphalia','Rhineland-Palatinate','Saarland','Saxony','Saxony-Anhalt','Schleswig-Holstein','Thuringia']
  },
  'France': {
    code: '+33',
    states: ['Auvergne-Rhône-Alpes','Bourgogne-Franche-Comté','Brittany','Centre-Val de Loire','Corsica','Grand Est','Hauts-de-France','Île-de-France','Normandy','Nouvelle-Aquitaine','Occitanie','Pays de la Loire','Provence-Alpes-Côte d\'Azur']
  }
};

const STORAGE_KEY = 'chaleur_checkout_form';

const saveFormToStorage = (data) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
};
const loadFormFromStorage = () => {
  try {
    const d = sessionStorage.getItem(STORAGE_KEY);
    return d ? JSON.parse(d) : null;
  } catch { return null; }
};
const clearFormStorage = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, sessionId, clearCart, loading: cartLoading } = useCart();
  const { user, token, isAuthenticated } = useAuth();
  const { formatPrice, currency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shippingRates, setShippingRates] = useState(null);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [redeemDiscount, setRedeemDiscount] = useState(0);
  const [razorpayConfig, setRazorpayConfig] = useState(null);
  const pollRef = useRef(null);
  const razorpayRef = useRef(null);

  const [address, setAddress] = useState(() => {
    const saved = loadFormFromStorage();
    if (saved?.address) return saved.address;
    return {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'India'
    };
  });

  const [guestCheckout, setGuestCheckout] = useState(() => {
    const saved = loadFormFromStorage();
    return saved?.guestCheckout || false;
  });

  // Restore form from sessionStorage and clear it
  useEffect(() => {
    const saved = loadFormFromStorage();
    if (saved) {
      if (saved.address) setAddress(saved.address);
      if (saved.guestCheckout) setGuestCheckout(saved.guestCheckout);
      clearFormStorage();
    }
  }, []);

  // Auto-fill from user profile when authenticated
  useEffect(() => {
    if (user) {
      setAddress(prev => ({
        ...prev,
        first_name: prev.first_name || user.first_name || '',
        last_name: prev.last_name || user.last_name || '',
        email: prev.email || user.email || '',
        phone: prev.phone || user.phone || ''
      }));
    }
  }, [user]);

  // Fetch Razorpay config, loyalty balance, and saved addresses
  useEffect(() => {
    fetchRazorpayConfig();
    if (token) {
      fetchLoyaltyBalance();
      fetchSavedAddresses();
    }
  }, [token]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Refetch shipping when currency changes
  useEffect(() => {
    if (address.country && address.postal_code) {
      fetchShippingRates(address.country, address.postal_code);
    }
  }, [currency]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSavedAddresses = async () => {
    try {
      const response = await axios.get(`${API}/orders/my-orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1 }
      });
      if (response.data.orders?.length > 0) {
        const lastAddress = response.data.orders[0].shipping_address;
        if (lastAddress) {
          setAddress(prev => ({
            ...prev,
            first_name: prev.first_name || lastAddress.first_name || '',
            last_name: prev.last_name || lastAddress.last_name || '',
            phone: prev.phone || lastAddress.phone || '',
            address_line1: prev.address_line1 || lastAddress.address_line1 || '',
            address_line2: prev.address_line2 || lastAddress.address_line2 || '',
            city: prev.city || lastAddress.city || '',
            state: prev.state || lastAddress.state || '',
            postal_code: prev.postal_code || lastAddress.postal_code || '',
            country: prev.country || lastAddress.country || 'India'
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching saved addresses:', error);
    }
  };

  const fetchRazorpayConfig = async () => {
    try {
      const response = await axios.get(`${API}/payment/config`);
      setRazorpayConfig(response.data);
    } catch (error) {
      console.error('Error fetching payment config:', error);
    }
  };

  const fetchLoyaltyBalance = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoyaltyData(response.data);
    } catch (error) {
      console.error('Error fetching loyalty balance:', error);
    }
  };

  const handleRedeemPoints = (points) => {
    const maxRedeemable = Math.min(points, loyaltyData?.points || 0);
    const subtotal = cart?.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
    const finalPoints = Math.min(maxRedeemable, subtotal);
    setPointsToRedeem(finalPoints);
    setRedeemDiscount(finalPoints);
  };

  const handleAddressChange = (field, value) => {
    setAddress(prev => {
      const updated = { ...prev, [field]: value };
      // Reset state when country changes
      if (field === 'country') updated.state = '';
      return updated;
    });

    if (field === 'country' || (field === 'postal_code' && value.length >= 5)) {
      fetchShippingRates(
        field === 'country' ? value : address.country,
        field === 'postal_code' ? value : address.postal_code
      );
    }
  };

  const fetchShippingRates = async (country, pincode) => {
    try {
      const response = await axios.post(`${API}/shipping/calculate-rates`, {
        delivery_pincode: pincode || address.postal_code || '110001',
        delivery_country: country,
        weight: 0.5
      }, { params: { currency } });
      setShippingRates(response.data);
      // Auto-select first rate when rates array arrives
      if (response.data?.rates && Array.isArray(response.data.rates) && response.data.rates.length > 0) {
        const first = response.data.rates[0];
        setShippingMethod(`${first.partner}-${first.service}-0`);
      }
    } catch {
      try {
        const fallbackResponse = await axios.get(`${API}/shipping/rates`, {
          params: { country, weight: 0.5, currency }
        });
        setShippingRates(fallbackResponse.data);
      } catch (err) {
        console.error('Error fetching shipping rates:', err);
      }
    }
  };

  // Save form state and redirect to login
  const handleLoginRedirect = () => {
    saveFormToStorage({ address, guestCheckout: true });
    navigate('/login?redirect=/checkout');
  };

  // Start polling payment status for QR/UPI payments
  const startPaymentPolling = useCallback((orderId, orderNumber) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/payment/check-status/${orderId}`);
        if (res.data.payment_status === 'paid') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          // Close Razorpay modal if open
          if (razorpayRef.current) {
            try { razorpayRef.current.close(); } catch {}
          }
          setPaymentProcessing(false);
          toast.success('Payment successful!', { description: `Order #${orderNumber} confirmed` });
          clearCart();
          if (isAuthenticated) {
            navigate('/account?tab=orders');
          } else {
            navigate(`/order-confirmation/${orderNumber}`);
          }
        }
      } catch {}
    }, 3000);
  }, [clearCart, isAuthenticated, navigate]);

  const initiateRazorpayPayment = async (order) => {
    if (!razorpayConfig?.enabled || !razorpayConfig?.razorpay_key_id) {
      toast.error('Payment gateway is not configured');
      return;
    }
    setPaymentProcessing(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const paymentResponse = await axios.post(`${API}/payment/create-order`, {
        order_id: order.id,
        amount: order.total,
        currency: currency
      }, { headers });

      const { razorpay_order_id, amount, key_id } = paymentResponse.data;

      // Start polling for payment status (handles QR/UPI payments)
      startPaymentPolling(order.id, order.order_number);

      const phoneWithCode = address.phone.startsWith('+')
        ? address.phone
        : `${COUNTRY_DATA[address.country]?.code || '+91'}${address.phone.replace(/\s+/g, '')}`;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Chaleur',
        description: `Order #${order.order_number}`,
        image: '/images/brand/chaleur-logo-black.png',
        order_id: razorpay_order_id,
        handler: async function(response) {
          // Stop polling since handler fired
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          try {
            const hdrs = token ? { Authorization: `Bearer ${token}` } : {};
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order.id
            }, { headers: hdrs });

            if (verifyResponse.data.success) {
              toast.success('Payment successful!', { description: `Order #${order.order_number} confirmed` });
              clearCart();
              if (isAuthenticated) {
                navigate('/account?tab=orders');
              } else {
                navigate(`/order-confirmation/${order.order_number}`);
              }
            }
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            console.error('Payment verification error:', error);
          } finally {
            setPaymentProcessing(false);
          }
        },
        prefill: {
          name: `${address.first_name} ${address.last_name}`,
          email: address.email,
          contact: phoneWithCode
        },
        notes: {
          order_id: order.id,
          order_number: order.order_number
        },
        theme: { color: '#1a1a1a' },
        modal: {
          ondismiss: function() {
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
            setPaymentProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpayRef.current = razorpay;
      razorpay.on('payment.failed', function(response) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        toast.error('Payment failed', { description: response.error.description });
        setPaymentProcessing(false);
      });
      razorpay.open();
    } catch (error) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      toast.error('Failed to initiate payment');
      console.error('Payment initiation error:', error);
      setPaymentProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated && !guestCheckout) return;
    if (!isAuthenticated && !address.email) {
      toast.error('Please enter your email address');
      return;
    }

    const requiredFields = ['first_name', 'last_name', 'phone', 'address_line1', 'city', 'state', 'postal_code', 'country'];
    const missingFields = requiredFields.filter(field => !address[field]);
    if (missingFields.length > 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const phoneWithCode = address.phone.startsWith('+')
        ? address.phone
        : `${COUNTRY_DATA[address.country]?.code || '+91'}${address.phone.replace(/\s+/g, '')}`;

      const shippingAddress = {
        type: 'shipping',
        first_name: address.first_name,
        last_name: address.last_name,
        phone: phoneWithCode,
        address_line1: address.address_line1,
        address_line2: address.address_line2,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        country: address.country
      };

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/orders`, {
        shipping_address: shippingAddress,
        billing_address: sameAsBilling ? null : shippingAddress,
        payment_method: 'razorpay',
        currency,
        notes: '',
        guest_email: !isAuthenticated ? address.email : undefined
      }, {
        params: { session_id: sessionId },
        headers
      });

      const order = response.data.order;
      setLoading(false);

      if (razorpayConfig?.enabled) {
        await initiateRazorpayPayment(order);
      } else {
        toast.success('Order placed successfully!', {
          description: `Order #${order.order_number}. Payment pending.`
        });
        clearCart();
        navigate('/account?tab=orders');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
      setLoading(false);
    }
  };

  if (cartLoading) {
    return (
      <div className="pt-32 pb-24 chaleur-container">
        <div className="animate-pulse space-y-8">
          <div className="h-10 bg-gray-100 rounded w-48" />
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-40 bg-gray-100 rounded" />
              <div className="h-40 bg-gray-100 rounded" />
            </div>
            <div className="h-80 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!cart?.items?.length) {
    return (
      <div className="pt-32 pb-24 chaleur-container text-center">
        <h1 className="text-2xl mb-4">Your cart is empty</h1>
        <Button asChild className="rounded-none">
          <Link to="/shop">Continue Shopping</Link>
        </Button>
      </div>
    );
  }

  const getShippingCost = () => {
    if (shippingRates?.rates && Array.isArray(shippingRates.rates)) {
      const selectedRate = shippingRates.rates.find(
        (r, idx) => `${r.partner}-${r.service}-${idx}` === shippingMethod
      );
      if (selectedRate) return selectedRate.total_cost;
    }
    if (shippingRates?.rates?.[shippingMethod]) {
      return shippingRates.rates[shippingMethod];
    }
    return address.country === 'India' ? 88 : 1650;
  };

  const shippingCost = getShippingCost();
  const subtotal = cart.subtotal;
  const tax = Math.round(subtotal * 0.18);
  const total = subtotal + shippingCost + tax - redeemDiscount;
  const countryPhoneCode = COUNTRY_DATA[address.country]?.code || '+91';
  const countryStates = COUNTRY_DATA[address.country]?.states || [];

  return (
    <div className="pt-32 pb-24" data-testid="checkout-page">
      <div className="chaleur-container">
        <h1 className="text-3xl md:text-4xl font-light mb-12">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">

              {/* Guest/Login Option */}
              {!isAuthenticated && !guestCheckout && (
                <section className="p-6 border border-gray-200 bg-gray-50" data-testid="checkout-auth-section">
                  <h2 className="text-xl font-medium mb-4">How would you like to checkout?</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div
                      className="p-4 border-2 border-gray-200 hover:border-primary cursor-pointer transition-colors text-center"
                      onClick={handleLoginRedirect}
                      data-testid="checkout-signin-btn"
                    >
                      <h3 className="font-medium mb-2">Sign In</h3>
                      <p className="text-sm text-muted-foreground">Access your account, saved addresses & earn loyalty points</p>
                    </div>
                    <div
                      className="p-4 border-2 border-gray-200 hover:border-primary cursor-pointer transition-colors text-center"
                      onClick={() => setGuestCheckout(true)}
                      data-testid="checkout-guest-btn"
                    >
                      <h3 className="font-medium mb-2">Continue as Guest</h3>
                      <p className="text-sm text-muted-foreground">Checkout quickly without creating an account</p>
                    </div>
                  </div>
                </section>
              )}

              {(isAuthenticated || guestCheckout) && (
                <>
                  {!isAuthenticated && guestCheckout && (
                    <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200">
                      <p className="text-sm text-blue-700">
                        Checking out as guest.{' '}
                        <button type="button" onClick={handleLoginRedirect} className="underline font-medium">Sign in</button>
                        {' '}to earn loyalty points!
                      </p>
                      <button type="button" onClick={() => setGuestCheckout(false)} className="text-blue-700 text-sm underline">Change</button>
                    </div>
                  )}

                  {/* Country Selection - FIRST */}
                  <section data-testid="country-section">
                    <h2 className="text-xl font-medium mb-6">Delivery Country</h2>
                    <Select
                      value={address.country}
                      onValueChange={(value) => handleAddressChange('country', value)}
                    >
                      <SelectTrigger className="rounded-none" data-testid="country-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(COUNTRY_DATA).map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </section>

                  {/* Contact Information */}
                  <section data-testid="contact-section">
                    <h2 className="text-xl font-medium mb-6">Contact Information</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-sm mb-2 block">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={address.email}
                          onChange={(e) => handleAddressChange('email', e.target.value)}
                          className="rounded-none"
                          required
                          placeholder="your@email.com"
                          data-testid="checkout-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-sm mb-2 block">Phone *</Label>
                        <div className="flex">
                          <span className="inline-flex items-center px-3 border border-r-0 border-input bg-muted text-sm text-muted-foreground min-w-[60px] justify-center select-none" data-testid="phone-country-code">
                            {countryPhoneCode}
                          </span>
                          <Input
                            id="phone"
                            type="tel"
                            value={address.phone}
                            onChange={(e) => handleAddressChange('phone', e.target.value)}
                            className="rounded-none rounded-l-none border-l-0"
                            required
                            placeholder="98765 43210"
                            data-testid="checkout-phone"
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Shipping Address */}
                  <section data-testid="shipping-address-section">
                    <h2 className="text-xl font-medium mb-6">Shipping Address</h2>
                    <div className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="first_name" className="text-sm mb-2 block">First Name *</Label>
                          <Input
                            id="first_name"
                            value={address.first_name}
                            onChange={(e) => handleAddressChange('first_name', e.target.value)}
                            className="rounded-none"
                            required
                            data-testid="checkout-firstname"
                          />
                        </div>
                        <div>
                          <Label htmlFor="last_name" className="text-sm mb-2 block">Last Name *</Label>
                          <Input
                            id="last_name"
                            value={address.last_name}
                            onChange={(e) => handleAddressChange('last_name', e.target.value)}
                            className="rounded-none"
                            required
                            data-testid="checkout-lastname"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="address_line1" className="text-sm mb-2 block">Address *</Label>
                        <Input
                          id="address_line1"
                          value={address.address_line1}
                          onChange={(e) => handleAddressChange('address_line1', e.target.value)}
                          className="rounded-none"
                          placeholder="Street address"
                          required
                          data-testid="checkout-address1"
                        />
                      </div>
                      <div>
                        <Input
                          id="address_line2"
                          value={address.address_line2}
                          onChange={(e) => handleAddressChange('address_line2', e.target.value)}
                          className="rounded-none"
                          placeholder="Apartment, suite, etc. (optional)"
                          data-testid="checkout-address2"
                        />
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="state" className="text-sm mb-2 block">
                            {address.country === 'United Arab Emirates' ? 'Emirate' : 'State / Province'} *
                          </Label>
                          {countryStates.length > 0 ? (
                            <Select
                              value={address.state}
                              onValueChange={(value) => handleAddressChange('state', value)}
                            >
                              <SelectTrigger className="rounded-none" data-testid="checkout-state">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {countryStates.map(s => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              id="state"
                              value={address.state}
                              onChange={(e) => handleAddressChange('state', e.target.value)}
                              className="rounded-none"
                              required
                              data-testid="checkout-state"
                            />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="city" className="text-sm mb-2 block">City *</Label>
                          <Input
                            id="city"
                            value={address.city}
                            onChange={(e) => handleAddressChange('city', e.target.value)}
                            className="rounded-none"
                            required
                            data-testid="checkout-city"
                          />
                        </div>
                        <div>
                          <Label htmlFor="postal_code" className="text-sm mb-2 block">
                            {address.country === 'India' ? 'PIN Code' : 'Postal Code'} *
                          </Label>
                          <Input
                            id="postal_code"
                            value={address.postal_code}
                            onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                            className="rounded-none"
                            required
                            data-testid="checkout-pincode"
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Shipping Method */}
                  <section data-testid="shipping-method-section">
                    <h2 className="text-xl font-medium mb-6">Shipping Method</h2>
                    {shippingRates?.rates && Array.isArray(shippingRates.rates) ? (
                      <RadioGroup value={shippingMethod} onValueChange={setShippingMethod} className="space-y-3">
                        {shippingRates.rates.slice(0, 4).map((rate, idx) => {
                          const rateId = `${rate.partner}-${rate.service}-${idx}`;
                          const label = rate.service?.toLowerCase().includes('express')
                            ? 'Express Delivery'
                            : rate.service?.toLowerCase().includes('air')
                              ? 'Air Delivery'
                              : 'Standard Delivery';
                          return (
                            <div
                              key={rateId}
                              className={`flex items-center justify-between p-4 border transition-colors ${
                                shippingMethod === rateId ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <RadioGroupItem value={rateId} id={`shipping-${idx}`} data-testid={`shipping-option-${idx}`} />
                                <Label htmlFor={`shipping-${idx}`} className="cursor-pointer">
                                  <div className="font-medium">{label}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {rate.estimated_days} business days
                                    {rate.is_cod_available && <span className="ml-2 text-green-600">Cash on delivery available</span>}
                                  </div>
                                </Label>
                              </div>
                              <span className="font-medium">{formatPrice(rate.total_cost)}</span>
                            </div>
                          );
                        })}
                      </RadioGroup>
                    ) : (
                      <RadioGroup value={shippingMethod} onValueChange={setShippingMethod} className="space-y-3">
                        <div className={`flex items-center justify-between p-4 border transition-colors ${
                          shippingMethod === 'standard' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="flex items-center gap-3">
                            <RadioGroupItem value="standard" id="standard" />
                            <Label htmlFor="standard" className="cursor-pointer">
                              <div className="font-medium">Standard Shipping</div>
                              <div className="text-sm text-muted-foreground">
                                {shippingRates?.delivery_estimates?.standard || '5-7 business days'}
                                {shippingRates?.partners?.standard && ` via ${shippingRates.partners.standard}`}
                              </div>
                            </Label>
                          </div>
                          <span className="font-medium">{formatPrice(shippingRates?.rates?.standard || (address.country === 'India' ? 88 : 1650))}</span>
                        </div>
                        <div className={`flex items-center justify-between p-4 border transition-colors ${
                          shippingMethod === 'express' ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                        }`}>
                          <div className="flex items-center gap-3">
                            <RadioGroupItem value="express" id="express" />
                            <Label htmlFor="express" className="cursor-pointer">
                              <div className="font-medium">Express Shipping</div>
                              <div className="text-sm text-muted-foreground">
                                {shippingRates?.delivery_estimates?.express || '2-3 business days'}
                                {shippingRates?.partners?.express && ` via ${shippingRates.partners.express}`}
                              </div>
                            </Label>
                          </div>
                          <span className="font-medium">{formatPrice(shippingRates?.rates?.express || (address.country === 'India' ? 132 : 2750))}</span>
                        </div>
                      </RadioGroup>
                    )}
                    {shippingRates?.duties_note && (
                      <p className="mt-3 text-sm text-amber-600">{shippingRates.duties_note}</p>
                    )}
                  </section>

                  {/* Billing Address */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <Checkbox
                        id="same_billing"
                        checked={sameAsBilling}
                        onCheckedChange={setSameAsBilling}
                      />
                      <Label htmlFor="same_billing" className="cursor-pointer">
                        Billing address same as shipping
                      </Label>
                    </div>
                  </section>

                  {/* Loyalty Points */}
                  {isAuthenticated && loyaltyData && loyaltyData.points > 0 && (
                    <section className="p-4 bg-amber-50 border border-amber-200" data-testid="loyalty-section">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-5 h-5 text-amber-600" />
                        <h3 className="font-medium">Chaleur Club Points</h3>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        You have {loyaltyData.points} points available (₹{loyaltyData.points} value)
                      </p>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          min="0"
                          max={Math.min(loyaltyData.points, subtotal)}
                          value={pointsToRedeem}
                          onChange={(e) => handleRedeemPoints(parseInt(e.target.value) || 0)}
                          className="rounded-none w-32"
                          data-testid="loyalty-points-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-none"
                          onClick={() => handleRedeemPoints(Math.min(loyaltyData.points, subtotal))}
                          data-testid="use-all-points-btn"
                        >
                          Use All
                        </Button>
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6 p-6 bg-gray-50 border border-gray-200" data-testid="order-summary">
                <h2 className="text-xl font-medium">Order Summary</h2>

                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {cart.items.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover bg-gray-100"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{item.variant_name}</p>
                        <p className="text-sm">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-medium">{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Shipping</span>
                    <span>{formatPrice(shippingCost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>GST (18%)</span>
                    <span>{formatPrice(tax)}</span>
                  </div>
                  {redeemDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Points Discount</span>
                      <span>-{formatPrice(redeemDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-medium pt-2 border-t border-gray-200">
                    <span>Total</span>
                    <span data-testid="checkout-total">{formatPrice(total)}</span>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CreditCard className="w-4 h-4" />
                    <span>Secure payment via Razorpay</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="w-4 h-4" />
                    <span>SSL encrypted checkout</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Truck className="w-4 h-4" />
                    <span>Free shipping over ₹5,000</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-none h-12"
                  disabled={loading || paymentProcessing || (!isAuthenticated && !guestCheckout)}
                  data-testid="checkout-pay-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Order...</>
                  ) : paymentProcessing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing Payment...</>
                  ) : (
                    <>Pay {formatPrice(total)}<ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>

                {razorpayConfig?.enabled && (
                  <div className="text-center pt-2">
                    <img
                      src="https://badges.razorpay.com/badge-dark.png"
                      alt="Razorpay Trusted"
                      className="h-8 mx-auto opacity-50"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
